import {
  ArrowRight,
  CheckCircle,
  Users,
  Target,
  Zap,
  Heart,
  Sparkles,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { MagicCard } from "@/components/ui/magic-card";
import { OrbitingCircles } from "@/components/ui/orbiting-circles";
import Icons from "@/components/global/icons";
import { motion } from "framer-motion";

const AboutPage = () => {
  const stats = [
    { number: "10K+", label: "Active Users" },
    { number: "500+", label: "Companies Trust Us" },
    { number: "99.9%", label: "Uptime" },
    { number: "24/7", label: "Support" },
  ];

  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description:
        "We believe AI should empower marketers, not replace them. Our mission is to make advanced marketing technology accessible to businesses of all sizes.",
    },
    {
      icon: Zap,
      title: "Innovation First",
      description:
        "We're constantly pushing the boundaries of what's possible with AI and machine learning in marketing automation.",
    },
    {
      icon: Heart,
      title: "Customer-Centric",
      description:
        "Every feature we build starts with understanding our customers' real challenges and creating solutions that truly matter.",
    },
    {
      icon: Users,
      title: "Collaborative",
      description:
        "We believe the best results come from bringing together human creativity with AI-powered insights and automation.",
      layoutClass: "md:col-start-2",
    },
  ];

  const team = [
    {
      name: "Sarah Chen",
      role: "Marketing Manager",
      description:
        "Former marketing director at Fortune 500 companies with 15+ years of experience in digital marketing transformation.",
      image: "/images/team/sarah.jpg",
    },
    {
      name: "Marcus Rodriguez",
      role: "Chief HR Officer",
      description:
        "AI/ML expert and former senior engineer at leading tech companies. PhD in Computer Science from Stanford.",
      image: "/images/team/marcus.jpg",
    },
    {
      name: "Elena Kowalski",
      role: "Head of Product",
      description:
        "Product strategy leader with deep expertise in UX design and customer research. Previously at top SaaS companies.",
      image: "/images/team/elena.jpg",
    },
  ];

  return (
    <Wrapper className="py-20 relative" data-oid="ya.b1fo">
      {/* Enhanced Hero Section */}
      <div
        className="relative flex flex-col items-center justify-center w-full py-20"
        data-oid="mu:_zp:"
      >
        {/* Background blur effect */}
        <div
          className="absolute flex lg:hidden size-40 rounded-full bg-blue-500 blur-[10rem] top-0 left-1/2 -translate-x-1/2 -z-10"
          data-oid="g7h17w8"
        ></div>

        {/* Orbiting circles for desktop */}
        <Container
          className="hidden lg:flex absolute inset-0 top-0 mb-auto flex-col items-center justify-center w-full min-h-[60vh] -z-10"
          data-oid="bpnzh5_"
        >
          <OrbitingCircles speed={0.4} radius={200} data-oid="oa144dh">
            <Icons.circle1
              className="size-3 text-blue-500/60"
              data-oid="o:err--"
            />
            <Icons.circle2
              className="size-1 text-purple-500/70"
              data-oid="51kk6yn"
            />
          </OrbitingCircles>
          <OrbitingCircles speed={0.2} radius={300} data-oid=".mnabd6">
            <Icons.circle2
              className="size-1 text-pink-500/50"
              data-oid="w4d4y:i"
            />
            <Icons.circle1
              className="size-4 text-blue-500/50"
              data-oid="0z99i10"
            />
          </OrbitingCircles>
        </Container>

        <Container data-oid="4008xe1">
          <div
            className="text-center max-w-4xl mx-auto mb-16 relative z-10"
            data-oid="we75_f8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-oid="svb7fgf"
            >
              <div
                className="group relative grid overflow-hidden rounded-full px-4 py-2 shadow-[0_1000px_0_0_hsl(0_0%_15%)_inset] transition-colors duration-200 mx-auto mb-6 w-fit"
                data-oid="i_h_1aj"
              >
                <span data-oid="zf_3m11">
                  <span
                    className="spark mask-gradient absolute inset-0 h-[100%] w-[100%] animate-flip overflow-hidden rounded-full [mask:linear-gradient(white,_transparent_50%)] before:absolute before:aspect-square before:w-[200%] before:rotate-[-90deg] before:animate-rotate before:bg-[conic-gradient(from_0deg,transparent_0_340deg,white_360deg)] before:content-[''] before:[inset:0_auto_auto_50%] before:[translate:-50%_-15%]"
                    data-oid="gk7_n54"
                  />
                </span>
                <span
                  className="backdrop absolute inset-[1px] rounded-full bg-background transition-colors duration-200 group-hover:bg-neutral-800"
                  data-oid="8u88v8k"
                />
                <span
                  className="z-10 py-1 text-sm text-neutral-100 flex items-center"
                  data-oid="iymf0hs"
                >
                  <Heart
                    className="h-4 w-4 mr-2 text-pink-500"
                    data-oid="i68yh7b"
                  />
                  Our Story & Mission
                </span>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 !leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-oid="ihg6.gh"
            >
              Transforming Marketing with{" "}
              <span
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                data-oid="j34zon-"
              >
                AI Innovation
              </span>
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-oid="26t6kxb"
            >
              We're building the future of marketing automation, where AI
              amplifies human creativity and data-driven insights lead to
              extraordinary results.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              data-oid="tnxdejx"
            >
              <Link to="/contact" className="group" data-oid="boae8v8">
                <Button
                  size="lg"
                  className="px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/25"
                  data-oid="3p483f_"
                >
                  Get in Touch
                  <ArrowRight
                    className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                    data-oid="3y0f4w1"
                  />
                </Button>
              </Link>
              <Link to="/demo" data-oid="6ke38ee">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 border-border/50 hover:bg-background/80 backdrop-blur-sm"
                  data-oid="rmcbl69"
                >
                  Start Free Trial
                </Button>
              </Link>
            </motion.div>
          </div>
        </Container>
      </div>

      {/* Enhanced Stats Section */}
      <Container data-oid="usw8xxm">
        <MagicCard
          gradientFrom="#0f172a"
          gradientTo="#1e1b4b"
          gradientColor="rgba(15,23,42,0.9)"
          className="relative overflow-hidden mb-20 rounded-3xl border border-white/10 bg-slate-950 text-white"
          data-oid="_zy__pe"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/60 via-purple-600/60 to-pink-600/40 opacity-80" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.35),_transparent_60%)] opacity-70" />
          <div className="relative p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-white/60 mb-2">
                  Platform by the numbers
                </p>
                <h2 className="text-3xl font-semibold leading-tight">
                  Proof that marketing teams trust Blyrha to scale responsibly
                </h2>
              </div>
              {/**
               * <Link to="/casestudy" className="inline-flex items-center text-sm font-semibold text-white/80 underline underline-offset-8 decoration-white/40 hover:text-white">
               *   See customer stories
               *   <ArrowRight className="ml-2 h-4 w-4" />
               * </Link>
               */}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center group rounded-2xl bg-white/5 p-6 backdrop-blur"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.04 }}
                  data-oid="s3se1ts"
                >
                  <motion.div
                    className="text-4xl md:text-5xl font-bold text-white"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.2 }}
                    data-oid="0twninn"
                  >
                    {stat.number}
                  </motion.div>
                  <div
                    className="mt-2 text-sm text-white/70 group-hover:text-white"
                    data-oid="nqj235i"
                  >
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </MagicCard>
      </Container>

      {/* Our Story Section */}
      <Container data-oid="hrnkb7r">
        <div className="mb-20" data-oid="1d-2md6">
          <div
            className="grid md:grid-cols-2 gap-12 items-center"
            data-oid="6z5hn24"
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              data-oid="o4p5usp"
            >
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                data-oid="ijt_94x"
              >
                Our Story
              </h2>
              <div
                className="space-y-4 text-muted-foreground"
                data-oid=":vtuc75"
              >
                <p data-oid="2u9ibkv">
                  Founded in 2024, Blyrha emerged from a simple observation:
                  marketing teams were spending countless hours on repetitive
                  tasks while struggling to extract meaningful insights from
                  their data.
                </p>
                <p data-oid="okegis.">
                  Our founders, having experienced these challenges firsthand in
                  their previous roles, set out to create a solution that would
                  not just automate marketing tasks, but intelligently enhance
                  the creative process.
                </p>
                <p data-oid="jc8h9tj">
                  Today, we're proud to serve thousands of marketers worldwide,
                  helping them achieve better results with less effort through
                  the power of AI.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              data-oid="m::o6so"
            >
              <img
                src="/images/about-story.jpg"
                alt="Our Story"
                className="rounded-2xl shadow-2xl w-full h-auto"
                data-oid="at492yj"
              />

              <div
                className="absolute inset-0 bg-gradient-to-t from-blue-600/10 to-transparent rounded-2xl"
                data-oid="gk1vqdm"
              ></div>
            </motion.div>
          </div>
        </div>
      </Container>

      {/* Values Section */}
      <Container data-oid="vg5j6a9">
        <div className="text-center mb-16" data-oid="dx-j_m:">
          <motion.h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            data-oid="ynfb1-q"
          >
            Our{" "}
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="7zf_ynz"
            >
              Values
            </span>
          </motion.h2>
          <motion.p
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            data-oid=":_e:1ir"
          >
            The principles that guide everything we do
          </motion.p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 mb-20" data-oid="4ddnnvo">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className={value.layoutClass ?? ""}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              data-oid="ytts.8-"
            >
              <MagicCard
                gradientFrom="#3b82f6"
                gradientTo="#8b5cf6"
                gradientColor="rgba(59,130,246,0.05)"
                className="text-center p-6 rounded-xl border bg-card/50 backdrop-blur-sm h-full"
                data-oid="-ehko7q"
              >
                <motion.div
                  className="mb-4 flex justify-center"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                  data-oid="lhshqi_"
                >
                  <value.icon
                    className="h-12 w-12 text-blue-600"
                    data-oid="l4c8qae"
                  />
                </motion.div>
                <h3 className="text-xl font-semibold mb-3" data-oid="fgqt2j-">
                  {value.title}
                </h3>
                <p className="text-muted-foreground" data-oid="lbvnk1u">
                  {value.description}
                </p>
              </MagicCard>
            </motion.div>
          ))}
        </div>
      </Container>
      
      {/* How to Contact Us Section */}
      <Container data-oid="gm0p93j">
        <div className="mb-20" data-oid="4g4l3ot">
          <div
            className="grid md:grid-cols-2 gap-12 items-center"
            data-oid="6o15vc0"
          >
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              data-oid="v94mv5s"
            >
              <div className="overflow-hidden rounded-2xl border border-foreground/10 shadow-2xl">
                <div className="aspect-[16/9] w-full">
                  <img
                    src="/showcase.gif"
                    alt="Contact Blyrha support"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-blue-600/10 via-transparent to-purple-600/20 pointer-events-none" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              data-oid="d67qa1n"
            >
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                data-oid="3ta1kz6"
              >
                How to Contact Us
              </h2>
              <p
                className="text-muted-foreground mb-6"
                data-oid="e2k7a5m"
              >
                Whether you need onboarding support, want to explore a custom
                integration, or simply have a question about Blyrha, our team is
                just a message away. Choose the channel that fits you best and
                we’ll respond within one business day.
              </p>
              <div
                className="grid sm:grid-cols-2 gap-4"
                data-oid="ok8w7qz"
              >
                <div className="rounded-2xl border border-border/50 bg-card/60 p-5 backdrop-blur-sm">
                  <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
                    Email
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    contact@blyrha.com
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Perfect for product questions and partnership inquiries.
                  </p>
                </div>
                <div className="rounded-2xl border border-border/50 bg-card/60 p-5 backdrop-blur-sm">
                  <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
                    Live Demo
                  </p>
                  <p className="text-lg font-semibold text-foreground">
                    Schedule a call
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Book a 30-minute walkthrough with our solution engineers.
                  </p>
                </div>
              </div>
              {/*<div className="mt-6 flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="px-8">
                    Contact Support
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="px-8">
                    Book a Demo
                  </Button>
                </Link>
              </div>*/}
            </motion.div>
          </div>
        </div>
      </Container>

      {/* <Container data-oid="az3td4d">
        <div className="text-center mb-16" data-oid="7vhw3y8">
          <motion.h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            data-oid="sib7szt"
          >
            Meet Our{" "}
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="pexffiz"
            >
              Team
            </span>
          </motion.h2>
          <motion.p
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            data-oid="_ow29az"
          >
            The passionate people behind Blyrha
          </motion.p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 mb-20" data-oid="j_zfeom">
          {team.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              data-oid="rbt231."
            >
              <MagicCard
                gradientFrom="#3b82f6"
                gradientTo="#8b5cf6"
                gradientColor="rgba(59,130,246,0.05)"
                className="text-center p-6 rounded-xl border bg-card/50 backdrop-blur-sm h-full"
                data-oid="gvl011j"
              >
                <motion.div
                  className="relative mb-6 mx-auto w-32 h-32 rounded-full overflow-hidden bg-gradient-to-b from-blue-100 to-purple-100"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  data-oid="2tjg.wg"
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-300"
                    onError={(e) => {
                      // Fallback to gradient background if image fails to load
                      e.currentTarget.style.display = "none";
                    }}
                    data-oid="v.9g.d4"
                  />

                  <div
                    className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent rounded-full"
                    data-oid="53pbt:m"
                  ></div>
                </motion.div>
                <h3 className="text-xl font-semibold mb-2" data-oid="cewenn1">
                  {member.name}
                </h3>
                <p
                  className="text-blue-600 font-medium mb-3"
                  data-oid="x0_tvbo"
                >
                  {member.role}
                </p>
                <p className="text-muted-foreground text-sm" data-oid="bsk-90a">
                  {member.description}
                </p>
              </MagicCard>
            </motion.div>
          ))}
        </div>
      </Container> */}

      {/* Enhanced CTA Section */}
      <Container data-oid="oun0x9:">
        <motion.div
          className="relative text-center p-12 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="17c1dal"
        >
          {/* Animated background elements */}
          <motion.div
            className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{ duration: 4, repeat: Infinity }}
            data-oid="t0oex2x"
          />

          <motion.div
            className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            data-oid="991a9n5"
          />

          <div className="relative z-10" data-oid="q8yon34">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-oid="9.8g5-2"
            >
              <Activity
                className="h-12 w-12 mx-auto mb-4 text-white/80"
                data-oid="6:ytjsh"
              />
            </motion.div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
              data-oid="4d:8c7x"
            >
              Ready to Transform Your Marketing?
            </h2>
            <p
              className="text-xl mb-8 opacity-90 max-w-2xl mx-auto"
              data-oid="at7bluv"
            >
              Join thousands of marketers who are already using AI to achieve
              extraordinary results.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              data-oid="3mrx4lq"
            >
              <Link to="/" data-oid="q1xb6ad">
                <Button
                  size="lg"
                  variant="secondary"
                  className="px-8 bg-white text-blue-600 hover:bg-white/90"
                  data-oid="wct_kpe"
                >
                  Get Started Free
                </Button>
              </Link>
              <Link to="/contact" data-oid="_e6ji6t">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 border-white text-white hover:bg-white/10 backdrop-blur-sm"
                  data-oid="u6:8nfj"
                >
                  Schedule a Demo
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </Container>
    </Wrapper>
  );
};

export default AboutPage;
